import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [showModal, setShowModal] = useState(false);
  const [showTallyForm, setShowTallyForm] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [visibleSections, setVisibleSections] = useState({});

  // For animated section reveals on scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => ({
            ...prev,
            [entry.target.id]: true
          }));
        }
      });
    }, { threshold: 0.1 });

    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach(section => {
      observer.observe(section);
    });

    return () => {
      sections.forEach(section => {
        observer.unobserve(section);
      });
    };
  }, []);

  // Load Tally form when modal is opened
  useEffect(() => {
    if (showModal && showTallyForm) {
      // Ensure the Tally script is loaded and executed
      const script = document.createElement('script');
      script.src = 'https://tally.so/widgets/embed.js';
      script.async = true;
      script.onload = () => {
        // Manually trigger Tally to load the form
        if (window.Tally) {
          window.Tally.loadEmbeds();
        }
      };
      document.body.appendChild(script);

      return () => {
        // Clean up
        document.body.removeChild(script);
      };
    }
  }, [showModal, showTallyForm]);

  const toggleModal = () => {
    setShowModal(!showModal);
    if (!showModal) {
      setShowTallyForm(false); // Reset to first step when opening modal
    }
  };

  const showForm = () => {
    setShowTallyForm(true);
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="App">
      <header className="site-header" role="banner">
        <nav className="navbar" aria-label="Main Navigation">
          <div className="container">
            <div className="logo">
              <img src="/complynest.svg" alt="ComplyNest Logo" className="logo-image" width="40" height="40" />
              <span className="logo-text">ComplyNest</span>
            </div>
            <button className="cta-button-primary navbar-cta" onClick={toggleModal} aria-label="Join the waitlist">Join the waitlist</button>
          </div>
        </nav>

        <section className="hero">
          <div className="container">
            <h1>Stay compliant without the chaos</h1>
            <h2>One place to manage your EU compliance, starting with EPR and CSRD reporting</h2>
            <p className="hero-subtitle">Built for busy European businesses</p>
            <p className="hero-cta-text">We're in early build mode and looking for sharp feedback. Join the waitlist and help shape what we launch first.</p>
            <button className="cta-button-primary" onClick={toggleModal} aria-label="Join the waitlist">Join the waitlist</button>
          </div>
        </section>
      </header>

      <main id="main-content">
        <section id="features" className={`features animate-on-scroll ${visibleSections.features ? 'visible' : ''}`} aria-labelledby="features-heading">
          <div className="container">
            <h2 id="features-heading">Simplify Your Compliance Journey</h2>
            
            <div className="feature-cards">
              <article className="feature-card interactive">
                <div className="feature-icon" aria-hidden="true">
                  <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 12H15" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 16H13" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="feature-content">
                  <h3>EPR Filing Assistant</h3>
                  <p>Calculate, track, and file packaging Extended Producer Responsibility fees across EU countries from a single dashboard, saving hours of complex research and paperwork.</p>
                </div>
                <div className="feature-hover-content">
                  <ul>
                    <li>Starting with Germany, France, and Spain; more countries coming soon</li>
                    <li>Simplifying filing processes with clear guidance</li>
                    <li>Smart deadline reminders to keep you on track</li>
                    <li>Early users help shape our roadmap and priority features</li>
                  </ul>
                </div>
              </article>

              <article className="feature-card interactive">
                <div className="feature-icon" aria-hidden="true">
                  <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 6H21" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 12H21" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 18H21" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 6H3.01" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 12H3.01" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 18H3.01" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="feature-content">
                  <h3>CSRD Report Auto-Fill</h3>
                  <p>Generate ESG-compliant sustainability reports automatically based on your existing accounting data, energy bills, and operations, reducing manual reporting effort by up to 80%.</p>
                </div>
                <div className="feature-hover-content">
                  <ul>
                    <li>Focusing on the most important metrics first</li>
                    <li>Our roadmap includes CSRD-ready templates</li>
                    <li>Simplified data collection process</li>
                    <li>Launching with core features and expanding based on user feedback</li>
                  </ul>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section id="product-visual" className={`product-visual animate-on-scroll ${visibleSections['product-visual'] ? 'visible' : ''}`} aria-labelledby="product-visual-heading">
          <div className="container">
            <h2 id="product-visual-heading">Compliance, boiled down to your next actions</h2>
            <p className="product-intro">Forget dashboards. ComplyNest gives you a single, lightweight "Action Feed". Each card tells you exactly what to do, why it matters, and when it's due - nothing more, nothing less.</p>
            <div className="dashboard-preview">
              <div className="dashboard-mockup action-feed">
                <div className="early-access-badge">In development - your feedback shapes this feed</div>
                <div className="action-cards">
                  <article className="action-card">
                    <div className="action-content">
                      <h4>Germany EPR filing due 31 May</h4>
                      <p>All figures pre-calculated, one click to generate the PDF.</p>
                    </div>
                    <button className="action-button" aria-label="Generate report">Generate report</button>
                  </article>
                  
                  <article className="action-card">
                    <div className="action-content">
                      <h4>France registration incomplete</h4>
                      <p>One form left. Estimated time: 5 min.</p>
                    </div>
                    <button className="action-button" aria-label="Finish now">Finish now</button>
                  </article>
                  
                  <article className="action-card">
                    <div className="action-content">
                      <h4>CSRD data 68% complete</h4>
                      <p>Upload last month's energy bill to finalise Scope 1 numbers.</p>
                    </div>
                    <button className="action-button" aria-label="Upload bill">Upload bill</button>
                  </article>
                </div>
                <a href="#features" className="add-more-link" aria-label="Add another country or regulation">Add another country or regulation →</a>
              </div>
            </div>
          </div>
        </section>

        <section id="trust" className={`trust animate-on-scroll ${visibleSections.trust ? 'visible' : ''}`} aria-labelledby="trust-heading">
          <div className="container">
            <h2 id="trust-heading">Why Trust Us</h2>
            <div className="trust-points">
              <article className="trust-item">
                <h3>Built by operators, not bureaucrats</h3>
                <p>We know the pain of messy compliance because we've lived it - managing logistics, reporting, and product data across borders.</p>
              </article>
              <article className="trust-item">
                <h3>Simple by design</h3>
                <p>No dashboards, no jargon. We focus on clarity, automation, and just-enough guidance so you never miss a deadline.</p>
              </article>
              <article className="trust-item">
                <h3>You're not alone</h3>
                <p>Behind the software is a real person. We support every customer personally while we build this product together - and yes, we actually reply.</p>
              </article>
            </div>
          </div>
        </section>

        <section id="faq" className={`faq animate-on-scroll ${visibleSections.faq ? 'visible' : ''}`} aria-labelledby="faq-heading">
          <div className="container">
            <h2 id="faq-heading">Frequently Asked Questions</h2>
            <div className="faq-items">
              <article className={`faq-item ${expandedFaq === 0 ? 'expanded' : ''}`}>
                <div className="faq-question" onClick={() => toggleFaq(0)} role="button" aria-expanded={expandedFaq === 0} aria-controls="faq-answer-0">
                  <h3>Who is this for?</h3>
                  <span className="faq-icon" aria-hidden="true">{expandedFaq === 0 ? '−' : '+'}</span>
                </div>
                <div className="faq-answer" id="faq-answer-0">
                  <p>ComplyNest is built for small and mid-sized businesses selling into Europe, especially e-commerce sellers, exporters, and product-based companies who need to meet packaging and sustainability rules without hiring a compliance team.</p>
                </div>
              </article>
              <article className={`faq-item ${expandedFaq === 1 ? 'expanded' : ''}`}>
                <div className="faq-question" onClick={() => toggleFaq(1)} role="button" aria-expanded={expandedFaq === 1} aria-controls="faq-answer-1">
                  <h3>What countries are supported?</h3>
                  <span className="faq-icon" aria-hidden="true">{expandedFaq === 1 ? '−' : '+'}</span>
                </div>
                <div className="faq-answer" id="faq-answer-1">
                  <p>We're starting with support for Germany, France, and Spain, with more EU markets on the way. You can join the waitlist and we'll let you know when your country is ready.</p>
                </div>
              </article>
              <article className={`faq-item ${expandedFaq === 2 ? 'expanded' : ''}`}>
                <div className="faq-question" onClick={() => toggleFaq(2)} role="button" aria-expanded={expandedFaq === 2} aria-controls="faq-answer-2">
                  <h3>Do I need to be a compliance expert?</h3>
                  <span className="faq-icon" aria-hidden="true">{expandedFaq === 2 ? '−' : '+'}</span>
                </div>
                <div className="faq-answer" id="faq-answer-2">
                  <p>No. We're designing ComplyNest so you can handle EPR filings or CSRD reports with minimal input. You'll get smart defaults, simple checklists, and plain-language guidance with real support if you ever get stuck.</p>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src="/complynest.svg" alt="ComplyNest Logo" className="footer-logo-image" width="30" height="30" />
              <span className="footer-logo-text">ComplyNest</span>
            </div>
          </div>
          <div className="footer-copyright">
            © 2025 ComplyNest. All rights reserved.
          </div>
        </div>
      </footer>

      {showModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-container">
            {!showTallyForm ? (
              <div className="personal-note intro-note">
                <p>Hi, I'm Nikhil. I'm building ComplyNest to kill the compliance headaches I've lived myself. This survey takes 60 seconds and your answers will decide what we build next. Thanks for shaping the product with me.</p>
                <button className="cta-button-primary proceed-button" onClick={showForm}>Sure, let's go!</button>
              </div>
            ) : (
              <div className="modal">
                <div className="modal-header">
                  <button className="close-button" onClick={toggleModal} aria-label="Close modal">×</button>
                </div>
                <div className="modal-body">
                  <div className="tally-form-container">
                    <iframe 
                      data-tally-src="https://tally.so/r/mYelQ5?transparentBackground=1&hideTitle=1" 
                      width="100%" 
                      height="100%" 
                      frameBorder="0" 
                      marginHeight="0" 
                      marginWidth="0" 
                      title="ComplyNest Waitlist"
                      loading="lazy"
                    ></iframe>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Back to top button */}
      <button 
        className={`back-to-top ${visibleSections.features ? 'visible' : ''}`}
        onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
        aria-label="Back to top"
      >
        ↑
      </button>
    </div>
  );
}

export default App;
